import type { MeResponse, RegisterResponse } from "../shared/api-types";
import { derivePasswordCredential } from "../shared/password";
import { apiRequest } from "./client";

export interface Credentials {
  email: string;
  password: string;
}

export function getMe() {
  return apiRequest<MeResponse>("/api/auth/me");
}

export async function login(input: Credentials) {
  const email = input.email.trim().toLowerCase();
  return apiRequest<{ user: RegisterResponse["user"]; csrfToken: string }>("/api/auth/login", {
    method: "POST",
    body: { ...input, email, password: await derivePasswordCredential(email, input.password) },
  });
}

export async function register(input: Credentials & { displayName: string }) {
  const email = input.email.trim().toLowerCase();
  return apiRequest<RegisterResponse>("/api/auth/register", {
    method: "POST",
    body: { ...input, email, password: await derivePasswordCredential(email, input.password) },
  });
}

export async function recover(input: { email: string; recoveryCode: string; newPassword: string }) {
  const email = input.email.trim().toLowerCase();
  return apiRequest<RegisterResponse>("/api/auth/recover", {
    method: "POST",
    body: { ...input, email, newPassword: await derivePasswordCredential(email, input.newPassword) },
  });
}

export function logout(csrfToken: string) {
  return apiRequest<{ loggedOut: true }>("/api/auth/logout", { method: "POST", csrfToken });
}

export async function changePassword(email: string, currentPassword: string, newPassword: string, csrfToken: string) {
  const [currentCredential, newCredential] = await Promise.all([
    derivePasswordCredential(email, currentPassword),
    derivePasswordCredential(email, newPassword),
  ]);
  return apiRequest<{ changed: true }>("/api/auth/change-password", {
    method: "POST",
    body: { currentPassword: currentCredential, newPassword: newCredential },
    csrfToken,
  });
}

export async function deleteAccount(email: string, password: string, csrfToken: string) {
  return apiRequest<{ deleted: true }>("/api/auth/account", {
    method: "DELETE",
    body: { password: await derivePasswordCredential(email, password), confirmation: "DELETE" },
    csrfToken,
  });
}
